// ============================================
// FAQ 标签管理页面（列表式，弹窗添加/编辑，删除确认）
// ============================================

import { useState, useEffect } from 'react';
import { X, Plus, Loader2, Edit3, Trash2, Globe, Check } from 'lucide-react';
import { useFaqStore } from './faq-store';
import type { FaqTag } from './types';

// ── 添加/编辑标签弹窗 ──
function TagModal({
  isOpen, onClose, editingTag, level, onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  editingTag: FaqTag | null;
  level: 1 | 2;
  onSubmit: (data: { name: string; name_en: string; level: 1 | 2 }) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editingTag) {
      setName(editingTag.name);
      setNameEn(editingTag.name_en || '');
    } else {
      setName('');
      setNameEn('');
    }
  }, [editingTag, isOpen]);

  async function handleSubmit() {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), name_en: nameEn.trim(), level });
      onClose();
    } catch (e: any) {
      console.error('Tag operation failed:', e);
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-xl p-6 relative">
        <h3 className="text-sm font-bold text-slate-700 mb-5 flex items-center gap-2">
          {editingTag ? (
            <><Edit3 size={15} className="text-sky-500" /> 编辑标签</>
          ) : (
            <><Plus size={15} className="text-sky-500" /> 添加{level === 1 ? '一级' : '二级'}标签</>
          )}
        </h3>
        <button onClick={onClose} className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors">
          <X size={16} />
        </button>

        <div className="space-y-4">
          {/* 中文名称 */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">
              🇨🇳 中文名称 <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={level === 1 ? '如：产品、售后、技术支持' : '如：CRM、CPQ、合同管理系统'}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            />
          </div>

          {/* 英文名称 */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1">
              <Globe size={12} /> 英文名称（可选）
            </label>
            <input
              type="text"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              placeholder={level === 1 ? 'e.g. Product, After-Sales, Tech Support' : 'e.g. CRM, CPQ, Contract Management'}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose}
            className="px-4 py-2 bg-slate-100 text-slate-500 text-sm font-medium rounded-xl hover:bg-slate-200 transition-all">
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim() || submitting}
            className="flex items-center gap-1.5 px-5 py-2 bg-sky-500 text-white text-sm font-semibold rounded-xl hover:bg-sky-600 disabled:opacity-50 transition-all"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {editingTag ? '保存' : '添加'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 删除确认弹窗 ──
function DeleteConfirmModal({
  isOpen, tagName, onConfirm, onCancel, deleting,
}: {
  isOpen: boolean;
  tagName: string;
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
}) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
      <div className="w-full max-w-xs bg-white border border-slate-200 rounded-2xl shadow-xl p-6 text-center">
        <div className="w-12 h-12 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
          <Trash2 size={20} className="text-red-500" />
        </div>
        <h3 className="text-sm font-bold text-slate-700 mb-2">确认删除标签</h3>
        <p className="text-xs text-slate-500 mb-5">
          确定要删除标签 <span className="font-semibold text-slate-700">「{tagName}」</span> 吗？<br />
          此操作不可撤销。
        </p>
        <div className="flex gap-2">
          <button onClick={onCancel}
            className="flex-1 px-4 py-2 bg-slate-100 text-slate-500 text-sm font-medium rounded-xl hover:bg-slate-200 transition-all">
            取消
          </button>
          <button onClick={onConfirm} disabled={deleting}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-xl hover:bg-red-600 disabled:opacity-50 transition-all">
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            删除
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 标签列表行 ──
function TagRow({ tag, onEdit, onDelete }: {
  tag: FaqTag;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="group flex items-center gap-3 px-4 py-3 bg-white border border-slate-100 rounded-xl hover:border-slate-200 hover:shadow-sm transition-all">
      {/* 标签名 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-slate-700">{tag.name}</span>
          {tag.name_en && (
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Globe size={10} />
              {tag.name_en}
            </span>
          )}
        </div>
      </div>
      {/* 排序序号 */}
      <span className="text-[10px] text-slate-300 font-mono">#{tag.sort_order}</span>
      {/* 操作按钮 */}
      <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
        <button
          onClick={onEdit}
          className="p-1.5 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-all"
          title="编辑"
        >
          <Edit3 size={13} />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
          title="删除"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ── 主页面 ──
export default function FaqTagsPage() {
  const {
    fetchTags, createTag, updateTag, deleteTag,
    getPrimaryTags, getSecondaryTags,
  } = useFaqStore();

  // 弹窗状态
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagModalLevel, setTagModalLevel] = useState<1 | 2>(1);
  const [editingTag, setEditingTag] = useState<FaqTag | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingTag, setDeletingTag] = useState<FaqTag | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  const primaryTags = getPrimaryTags();
  const secondaryTags = getSecondaryTags();

  // 打开添加弹窗
  function openAddModal(level: 1 | 2) {
    setEditingTag(null);
    setTagModalLevel(level);
    setTagModalOpen(true);
  }

  // 打开编辑弹窗
  function openEditModal(tag: FaqTag) {
    setEditingTag(tag);
    setTagModalLevel(tag.level);
    setTagModalOpen(true);
  }

  // 打开删除确认
  function openDeleteModal(tag: FaqTag) {
    setDeletingTag(tag);
    setDeleteModalOpen(true);
  }

  // 提交添加/编辑
  async function handleTagSubmit(data: { name: string; name_en: string; level: 1 | 2 }) {
    if (editingTag) {
      await updateTag(editingTag.id, { name: data.name, name_en: data.name_en || undefined });
    } else {
      await createTag(data.name, data.level, data.name_en || undefined);
    }
  }

  // 确认删除
  async function handleConfirmDelete() {
    if (!deletingTag) return;
    setDeleting(true);
    try {
      await deleteTag(deletingTag.id);
      setDeleteModalOpen(false);
      setDeletingTag(null);
    } catch (e: any) {
      console.error('Delete tag failed:', e);
    } finally {
      setDeleting(false);
    }
  }

  // 标签分类区块
  function TagSection({
    title, description, level, tags, color,
  }: {
    title: string; description: string; level: 1 | 2;
    tags: FaqTag[]; color: 'sky' | 'purple';
  }) {
    const bgColor = color === 'sky' ? 'bg-sky-50' : 'bg-purple-50';
    const textColor = color === 'sky' ? 'text-sky-600' : 'text-purple-600';
    const btnHover = color === 'sky' ? 'hover:bg-sky-100 hover:text-sky-700' : 'hover:bg-purple-100 hover:text-purple-700';

    return (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {/* 区块头 */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-bold text-slate-700">{title}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{description}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 ${bgColor} ${textColor} text-xs font-semibold rounded-lg`}>
              {tags.length} 个
            </span>
            <button
              onClick={() => openAddModal(level)}
              className={`flex items-center gap-1 px-3 py-1.5 ${bgColor} ${textColor} text-xs font-semibold rounded-lg ${btnHover} transition-all`}
            >
              <Plus size={13} />
              添加
            </button>
          </div>
        </div>

        {/* 标签列表 */}
        <div className="p-3">
          {tags.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-xs text-slate-400">暂无标签，点击上方按钮添加</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {tags.map((tag) => (
                <TagRow
                  key={tag.id}
                  tag={tag}
                  onEdit={() => openEditModal(tag)}
                  onDelete={() => openDeleteModal(tag)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-3xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800">标签管理</h1>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">管理 FAQ 的一级分类标签和二级系统来源标签，支持中英双语</p>
      </div>

      <div className="space-y-6">
        <TagSection
          title="一级标签（问题分类）"
          description="用于 FAQ 问题的主分类，如：产品、售后、技术支持等"
          level={1}
          tags={primaryTags}
          color="sky"
        />

        <TagSection
          title="二级标签（系统来源）"
          description="标记问题所属的系统，如：CRM、CPQ、合同管理系统等"
          level={2}
          tags={secondaryTags}
          color="purple"
        />
      </div>

      {/* 添加/编辑弹窗 */}
      <TagModal
        isOpen={tagModalOpen}
        onClose={() => { setTagModalOpen(false); setEditingTag(null); }}
        editingTag={editingTag}
        level={tagModalLevel}
        onSubmit={handleTagSubmit}
      />

      {/* 删除确认弹窗 */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        tagName={deletingTag?.name || ''}
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteModalOpen(false); setDeletingTag(null); }}
        deleting={deleting}
      />
    </div>
  );
}
